import React, { Component } from 'react'
import { Input, Button, Table, Modal, Popconfirm, Icon } from 'antd'
import { PermissionService } from 'services/PermissionService'
import { Permissions } from 'permissions'
import { TableAction } from 'components/TableAction'
import { PushNotification } from 'utils/PushNotification'
import { setContext, getContext, getAllContext, deleteContext } from 'services/CeberusApiService'
import { ContextRequest } from 'models/request/Index'
import { Tenant } from 'models/Blip/Tenant'
import { TenantService } from 'connection/TenantService'
import { SearchTable } from 'components/SearchTable'

const { Column } = Table;

const handleSearch = (selectedKeys: string, confirm: Function) => (): void => {
  confirm();
};

const handleReset = (clearFilters: Function) => (): void => {
  clearFilters();
};

export class Context extends Component<{ownerIdentity: string, contact: any, refreshKey?: number }> {

  tenantSelected: Tenant;

  state = {
    hasEditPermission: PermissionService.hasPermission(Permissions.Application_Contacts_State),
    ownerIdentity: this.props.ownerIdentity,
    context: '',
    chosenRow: { context : '' },
    isLoading: false,
    isModalVisible: false,
    isPopConfirmVisible: false,
    totalItems: 0,
    tableRows: []
  }

  showModal = (record: any) : void => {
    this.setState({ isModalVisible: true, chosenRow: record });
  }

  hideModal = () : void => {
    this.setState({ isModalVisible: false, chosenRow: '' })
  }

  componentDidMount() {
    this.tenantSelected = TenantService.getSelectedTenant();
    this.getContexts();
  }

  componentDidUpdate(prevProps: Readonly<{ ownerIdentity: string, contact: any, refreshKey?: number }>) {
    if (prevProps.refreshKey !== this.props.refreshKey) {
      this.getContexts();
    }
  }

  getAllContexts = async () : Promise<any> => {
    try {
      const resource = await getAllContext(new ContextRequest(
        this.props.contact.identity,
        String(),
        String(),
        this.tenantSelected.name,
        this.props.ownerIdentity)
      )
      return resource.items
    } catch (e) {
      return undefined
    }
  }

  deleteContext = async (record: any) : Promise<void>  => {
    try {
      const response = await deleteContext(new ContextRequest(
        this.props.contact.identity,
        record.context,
        String(),
        this.tenantSelected.name,
        this.props.ownerIdentity)
      );

      this.getContexts();
      PushNotification.success(response);
    } catch (error) {
      PushNotification.httpError(error);
    }
  }

  deleteContexts = async () : Promise<void> => {
    const specificContext = await this.getAllContexts();
    for (const context of specificContext) {
      try {
        await deleteContext(new ContextRequest(
          this.props.contact.identity,
          context,
          String(),
          this.tenantSelected.name,
          this.props.ownerIdentity)
        );
      } catch (error) {
        PushNotification.httpError(error);
      }
    }
    this.getContexts();
  }

  popConfirmVisibleChange = (isPopConfirmVisible: boolean) : void => {
    if (this.state.context) {
      this.setState({ isPopConfirmVisible });
    }
  }

  setComponentLoading = (isLoading: boolean) : void => this.setState({ isLoading });

  getContexts = async () : Promise<void> => {
    this.setComponentLoading(true);

    const contexts = await this.getAllContexts();

    if (contexts) {
      const tableRowsPromise = contexts.map(async (context : any) => {
        try {

          const resource = await getContext(new ContextRequest(
            this.props.contact.identity,
            context,
            String(),
            this.tenantSelected.name,
            this.state.ownerIdentity)
          );

          return {
            context: context,
            value: typeof resource == 'string' ? resource : JSON.stringify(resource)
          }
        } catch (error) {
          return {
            context: context,
            value: '-'
          }
        }
      })

      const tableRows = await Promise.all(tableRowsPromise);

      this.setState({ totalItems: tableRows.length, tableRows, context: '' });

    } else {
      this.setState({ totalItems: 0, tableRows: [], context: '' });
    }

    this.setComponentLoading(false);
  }

  setContext = async () : Promise<void> => {
    this.setComponentLoading(true);

    try {
      const response = await setContext(new ContextRequest(
        this.props.contact.identity,
        this.state.chosenRow.context,
        this.state.context,
        this.tenantSelected.name,
        this.state.ownerIdentity)
      );

      PushNotification.success(response);
    } catch (error) {
      PushNotification.error(error);
    }
    this.setState({ isModalVisible: false });
    this.setComponentLoading(false);
    this.getContexts();
  };

  filterByContext = (record: any, value: any): boolean => {
    const lowerCasedValue = value.toLowerCase();
    return record.context.toLowerCase().includes(lowerCasedValue);
  };

  render() {
    let searchInput;

    return (
      <div className="tc">
        <div className="mt5">
          <div className="flex justify-between mb3">
            <Popconfirm placement="top" disabled={this.state.tableRows.length === 0 || !this.state.hasEditPermission} title={'Are you sure you want to delete all contexts'} onConfirm={() => this.deleteContexts()} okText="Delete" cancelText="Cancel">
              <Button
                icon="delete"
                type="primary"
                disabled={this.state.tableRows.length === 0 || !this.state.hasEditPermission}>
                Delete All Contexts
              </Button>
            </Popconfirm>
          </div>
          <Table
            dataSource={this.state.tableRows}
            rowKey={record => record.userIdentity}
            loading={this.state.isLoading}
            expandedRowRender={(record: any) => (
              <p style={{ margin: 0, wordBreak: 'break-word' }}>{record.value}</p>
            )}>
            <Column
              title="Context"
              dataIndex="context"
              key="context"
              onFilter={(value, record) => this.filterByContext(record, value)}
              filterIcon={(filtered) => (
                <Icon type="search" style={{ color: filtered ? '#108ee9' : '#aaa' }} />
              )}
              filterDropdown={({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <SearchTable
                  onSetRef={(e) => (searchInput = e)}
                  onSetSelectedKeys={setSelectedKeys}
                  selectedKeys={selectedKeys}
                  confirm={confirm}
                  clearFilters={clearFilters}
                  onSearch={handleSearch}
                  onReset={handleReset}
                />
              )}
            />
            <Column
              title="Value"
              dataIndex="value"
              key="value"
              render={t => <p style={{ wordBreak: 'break-word' }}> {(t.length > 100 ? `${t.substring(0, 100)}...` : t)}</p>}
            />
            <Column title="Allowed Actions" dataIndex="action" key="action" render={(_, record: any) => (
              <TableAction
                onEdit={record.value === '-' || !this.state.hasEditPermission ? undefined : ()=> this.showModal(record)}
                onRemove={record.value === '-' || !this.state.hasEditPermission ? undefined : () => this.deleteContext(record)} />
            )}/>
          </Table>
        </div>
        {<Modal
          title="Edit Context Value"
          visible={this.state.isModalVisible}
          onCancel={() => this.setState({ isModalVisible: false })}
          footer={[
            <Button className="mr3" key="back" onClick={() => this.setState({ isModalVisible: false })}>Cancel</Button>,
            <Popconfirm
              key="confirm"
              visible={this.state.isPopConfirmVisible}
              onVisibleChange={this.popConfirmVisibleChange}
              title={`Are you sure you want to change the value of ${this.state.chosenRow.context} to '${this.state.context}'`}
              onConfirm={this.setContext}
              okText="Yes"
              cancelText="No">
              <Button
                disabled={this.state.context === ''}
                loading={this.state.isLoading}
                type="primary">
                Save
              </Button>
            </Popconfirm>
          ]}>
          <span>To which value you want to change {this.state.chosenRow.context}?</span>
          <Input
            name="stateId"
            value={this.state.context}
            onChange={event => this.setState({ context: event.target.value })}
            placeholder="Context Value"
            size="large" />
        </Modal>}
      </div>
    )
  }
}